import React, { useMemo, useState } from 'react';
import {
  Button,
  Card,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Select,
  Space,
  Table,
  Tabs,
  Tag,
  Typography,
  message,
} from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { epcEstimatingLibraryService } from '@/services/epcEstimatingLibrary.service';
import { formatMoney } from '@/utils/format';

const { Title, Text, Paragraph } = Typography;

export type EstimatingLibraryTab =
  | 'labour'
  | 'equipment'
  | 'crews'
  | 'norms'
  | 'assemblies'
  | 'rate-resolver';

const TAB_META: Record<EstimatingLibraryTab, { label: string; path: string }> = {
  labour: { label: 'Labour rates', path: '/epc/libraries/labour' },
  equipment: { label: 'Equipment rates', path: '/epc/libraries/equipment' },
  crews: { label: 'Crews', path: '/epc/libraries/crews' },
  norms: { label: 'Productivity norms', path: '/epc/libraries/norms' },
  assemblies: { label: 'Assemblies', path: '/epc/libraries/assemblies' },
  'rate-resolver': { label: 'Rate Resolver', path: '/epc/libraries/rate-resolver' },
};

function tabFromParam(raw?: string): EstimatingLibraryTab {
  if (raw && raw in TAB_META) return raw as EstimatingLibraryTab;
  return 'labour';
}

function fmtRate(v: unknown, currency?: string) {
  return `${formatMoney(Number(v) || 0, { display: 'plain', maximumFractionDigits: 2 })} ${currency || 'AED'}`;
}

function errMsg(e: unknown) {
  return e instanceof Error ? e.message : 'Request failed';
}

const RESOURCE_KIND_OPTS = [
  { value: 'MATERIAL', label: 'MATERIAL' },
  { value: 'LABOUR', label: 'LABOUR' },
  { value: 'EQUIPMENT', label: 'EQUIPMENT' },
  { value: 'SUBCONTRACT', label: 'SUBCONTRACT' },
  { value: 'CONSUMABLE', label: 'CONSUMABLE' },
  { value: 'OTHER', label: 'OTHER' },
];

const EpcEstimatingLibrariesPage: React.FC = () => {
  const { tab: tabParam } = useParams<{ tab?: string }>();
  const navigate = useNavigate();
  const active = tabFromParam(tabParam);
  const qc = useQueryClient();

  const [crewId, setCrewId] = useState<string | null>(null);
  const [assemblyId, setAssemblyId] = useState<string | null>(null);
  const [versionId, setVersionId] = useState<string | null>(null);

  // Modal / edit state
  const [modal, setModal] = useState<{ kind: string; record?: any } | null>(null);

  const [labourClassForm] = Form.useForm();
  const [labourRateForm] = Form.useForm();
  const [equipClassForm] = Form.useForm();
  const [equipRateForm] = Form.useForm();
  const [crewForm] = Form.useForm();
  const [memberForm] = Form.useForm();
  const [normForm] = Form.useForm();
  const [assemblyForm] = Form.useForm();
  const [componentForm] = Form.useForm();

  // ── Queries ──────────────────────────────────────────────────────────────
  const labourClassesQ = useQuery({
    queryKey: ['epc-est-lib-labour-classes'],
    queryFn: () => epcEstimatingLibraryService.listLabourClasses(),
    enabled: active === 'labour' || active === 'crews',
  });
  const labourRatesQ = useQuery({
    queryKey: ['epc-est-lib-labour-rates'],
    queryFn: () => epcEstimatingLibraryService.listLabourRates(),
    enabled: active === 'labour',
  });
  const equipClassesQ = useQuery({
    queryKey: ['epc-est-lib-equip-classes'],
    queryFn: () => epcEstimatingLibraryService.listEquipmentClasses(),
    enabled: active === 'equipment',
  });
  const equipRatesQ = useQuery({
    queryKey: ['epc-est-lib-equip-rates'],
    queryFn: () => epcEstimatingLibraryService.listEquipmentRates(),
    enabled: active === 'equipment',
  });
  const crewsQ = useQuery({
    queryKey: ['epc-est-lib-crews'],
    queryFn: () => epcEstimatingLibraryService.listCrewTemplates(),
    enabled: active === 'crews' || active === 'norms',
  });
  const crewMembersQ = useQuery({
    queryKey: ['epc-est-lib-crew-members', crewId],
    queryFn: () => epcEstimatingLibraryService.listCrewMembers(crewId!),
    enabled: active === 'crews' && !!crewId,
  });
  const normsQ = useQuery({
    queryKey: ['epc-est-lib-norms'],
    queryFn: () => epcEstimatingLibraryService.listProductivityNorms(),
    enabled: active === 'norms',
  });
  const assembliesQ = useQuery({
    queryKey: ['epc-est-lib-assemblies'],
    queryFn: () => epcEstimatingLibraryService.listAssemblies(),
    enabled: active === 'assemblies',
  });
  const assemblyDetailQ = useQuery({
    queryKey: ['epc-est-lib-assembly', assemblyId],
    queryFn: () => epcEstimatingLibraryService.getAssembly(assemblyId!),
    enabled: active === 'assemblies' && !!assemblyId,
  });
  const componentsQ = useQuery({
    queryKey: ['epc-est-lib-assembly-components', versionId],
    queryFn: () => epcEstimatingLibraryService.listAssemblyComponents(versionId!),
    enabled: active === 'assemblies' && !!versionId,
  });

  const labourClassOptions = useMemo(
    () =>
      (labourClassesQ.data || []).map((c: any) => ({
        value: c.id,
        label: `${c.code} — ${c.name}`,
      })),
    [labourClassesQ.data]
  );
  const equipClassOptions = useMemo(
    () =>
      (equipClassesQ.data || []).map((c: any) => ({
        value: c.id,
        label: `${c.code} — ${c.name}`,
      })),
    [equipClassesQ.data]
  );
  const crewOptions = useMemo(
    () =>
      (crewsQ.data || []).map((c: any) => ({
        value: c.id,
        label: `${c.code} — ${c.name}`,
      })),
    [crewsQ.data]
  );

  const versions: any[] = assemblyDetailQ.data?.versions || [];
  const selectedVersion = versions.find((v) => v.id === versionId);
  const versionIsDraft = String(selectedVersion?.status || '') === 'DRAFT';

  const closeModal = () => setModal(null);

  const openEdit = (kind: string, record: any, form: any, values: Record<string, unknown>) => {
    form.setFieldsValue(values);
    setModal({ kind, record });
  };

  // ── Mutations helper ─────────────────────────────────────────────────────
  const onMutOk = (msg: string, keys: string[][]) => {
    message.success(msg);
    closeModal();
    keys.forEach((k) => qc.invalidateQueries({ queryKey: k }));
  };
  const onMutErr = (e: unknown) => message.error(errMsg(e));

  // Labour class
  const labourClassSaveM = useMutation({
    mutationFn: async (v: Record<string, unknown>) => {
      if (modal?.record?.id) {
        return epcEstimatingLibraryService.updateLabourClass(modal.record.id, v);
      }
      return epcEstimatingLibraryService.createLabourClass(v);
    },
    onSuccess: () => {
      labourClassForm.resetFields();
      onMutOk(modal?.record ? 'Labour class updated' : 'Labour class created', [
        ['epc-est-lib-labour-classes'],
      ]);
    },
    onError: onMutErr,
  });
  const labourClassDelM = useMutation({
    mutationFn: (id: string) => epcEstimatingLibraryService.deleteLabourClass(id),
    onSuccess: () => onMutOk('Labour class deleted', [['epc-est-lib-labour-classes']]),
    onError: onMutErr,
  });

  // Labour rate
  const labourRateSaveM = useMutation({
    mutationFn: async (v: Record<string, unknown>) => {
      if (modal?.record?.id) {
        return epcEstimatingLibraryService.updateLabourRate(modal.record.id, v);
      }
      return epcEstimatingLibraryService.createLabourRate(v);
    },
    onSuccess: () => {
      labourRateForm.resetFields();
      onMutOk(modal?.record ? 'Labour rate updated' : 'Labour rate saved', [
        ['epc-est-lib-labour-rates'],
      ]);
    },
    onError: onMutErr,
  });
  const labourRateDelM = useMutation({
    mutationFn: (id: string) => epcEstimatingLibraryService.deleteLabourRate(id),
    onSuccess: () => onMutOk('Labour rate deleted', [['epc-est-lib-labour-rates']]),
    onError: onMutErr,
  });

  // Equipment class
  const equipClassSaveM = useMutation({
    mutationFn: async (v: Record<string, unknown>) => {
      if (modal?.record?.id) {
        return epcEstimatingLibraryService.updateEquipmentClass(modal.record.id, v);
      }
      return epcEstimatingLibraryService.createEquipmentClass(v);
    },
    onSuccess: () => {
      equipClassForm.resetFields();
      onMutOk(modal?.record ? 'Equipment class updated' : 'Equipment class created', [
        ['epc-est-lib-equip-classes'],
      ]);
    },
    onError: onMutErr,
  });
  const equipClassDelM = useMutation({
    mutationFn: (id: string) => epcEstimatingLibraryService.deleteEquipmentClass(id),
    onSuccess: () => onMutOk('Equipment class deleted', [['epc-est-lib-equip-classes']]),
    onError: onMutErr,
  });

  // Equipment rate
  const equipRateSaveM = useMutation({
    mutationFn: async (v: Record<string, unknown>) => {
      if (modal?.record?.id) {
        return epcEstimatingLibraryService.updateEquipmentRate(modal.record.id, v);
      }
      return epcEstimatingLibraryService.createEquipmentRate(v);
    },
    onSuccess: () => {
      equipRateForm.resetFields();
      onMutOk(modal?.record ? 'Equipment rate updated' : 'Equipment rate saved', [
        ['epc-est-lib-equip-rates'],
      ]);
    },
    onError: onMutErr,
  });
  const equipRateDelM = useMutation({
    mutationFn: (id: string) => epcEstimatingLibraryService.deleteEquipmentRate(id),
    onSuccess: () => onMutOk('Equipment rate deleted', [['epc-est-lib-equip-rates']]),
    onError: onMutErr,
  });

  // Crew
  const crewSaveM = useMutation({
    mutationFn: async (v: Record<string, unknown>) => {
      if (modal?.record?.id) {
        return epcEstimatingLibraryService.updateCrewTemplate(modal.record.id, {
          code: v.code,
          name: v.name,
          description: v.description,
        });
      }
      return epcEstimatingLibraryService.createCrewTemplate({
        code: v.code,
        name: v.name,
        description: v.description,
        members: [],
      });
    },
    onSuccess: () => {
      crewForm.resetFields();
      onMutOk(modal?.record ? 'Crew updated' : 'Crew created', [['epc-est-lib-crews']]);
    },
    onError: onMutErr,
  });
  const crewDelM = useMutation({
    mutationFn: (id: string) => epcEstimatingLibraryService.deleteCrewTemplate(id),
    onSuccess: (_, id) => {
      if (crewId === id) setCrewId(null);
      onMutOk('Crew deleted', [['epc-est-lib-crews']]);
    },
    onError: onMutErr,
  });

  // Crew member
  const memberSaveM = useMutation({
    mutationFn: async (v: Record<string, unknown>) => {
      if (!crewId) throw new Error('Select a crew first');
      if (modal?.record?.id) {
        return epcEstimatingLibraryService.updateCrewMember(crewId, modal.record.id, v);
      }
      return epcEstimatingLibraryService.addCrewMember(crewId, v);
    },
    onSuccess: () => {
      memberForm.resetFields();
      onMutOk(modal?.record ? 'Member updated' : 'Member added', [
        ['epc-est-lib-crew-members', crewId!],
      ]);
    },
    onError: onMutErr,
  });
  const memberDelM = useMutation({
    mutationFn: (id: string) => epcEstimatingLibraryService.deleteCrewMember(crewId!, id),
    onSuccess: () => onMutOk('Member deleted', [['epc-est-lib-crew-members', crewId!]]),
    onError: onMutErr,
  });

  // Norm
  const normSaveM = useMutation({
    mutationFn: async (v: Record<string, unknown>) => {
      if (modal?.record?.id) {
        return epcEstimatingLibraryService.updateProductivityNorm(modal.record.id, v);
      }
      return epcEstimatingLibraryService.createProductivityNorm(v);
    },
    onSuccess: () => {
      normForm.resetFields();
      onMutOk(modal?.record ? 'Norm updated' : 'Norm created', [['epc-est-lib-norms']]);
    },
    onError: onMutErr,
  });
  const normDelM = useMutation({
    mutationFn: (id: string) => epcEstimatingLibraryService.deleteProductivityNorm(id),
    onSuccess: () => onMutOk('Norm deleted', [['epc-est-lib-norms']]),
    onError: onMutErr,
  });

  // Assembly
  const assemblySaveM = useMutation({
    mutationFn: async (v: Record<string, unknown>) => {
      if (modal?.record?.id) {
        return epcEstimatingLibraryService.updateAssembly(modal.record.id, v);
      }
      return epcEstimatingLibraryService.createAssembly(v);
    },
    onSuccess: () => {
      assemblyForm.resetFields();
      const keys: string[][] = [['epc-est-lib-assemblies']];
      if (assemblyId) keys.push(['epc-est-lib-assembly', assemblyId]);
      onMutOk(modal?.record ? 'Assembly updated' : 'Assembly created', keys);
    },
    onError: onMutErr,
  });
  const assemblyDelM = useMutation({
    mutationFn: (id: string) => epcEstimatingLibraryService.deleteAssembly(id),
    onSuccess: (_, id) => {
      if (assemblyId === id) {
        setAssemblyId(null);
        setVersionId(null);
      }
      onMutOk('Assembly deleted', [['epc-est-lib-assemblies']]);
    },
    onError: onMutErr,
  });
  const versionCreateM = useMutation({
    mutationFn: () => epcEstimatingLibraryService.createAssemblyVersion(assemblyId!),
    onSuccess: (row: any) => {
      message.success('Draft version created');
      qc.invalidateQueries({ queryKey: ['epc-est-lib-assembly', assemblyId!] });
      qc.invalidateQueries({ queryKey: ['epc-est-lib-assemblies'] });
      if (row?.id) setVersionId(String(row.id));
    },
    onError: onMutErr,
  });
  const versionPublishM = useMutation({
    mutationFn: (vid: string) => epcEstimatingLibraryService.publishAssemblyVersion(vid),
    onSuccess: () => {
      message.success('Version published');
      qc.invalidateQueries({ queryKey: ['epc-est-lib-assembly', assemblyId!] });
      qc.invalidateQueries({ queryKey: ['epc-est-lib-assemblies'] });
      if (versionId) qc.invalidateQueries({ queryKey: ['epc-est-lib-assembly-components', versionId] });
    },
    onError: onMutErr,
  });

  // Component
  const componentSaveM = useMutation({
    mutationFn: async (v: Record<string, unknown>) => {
      if (!versionId) throw new Error('Select a version first');
      if (modal?.record?.id) {
        return epcEstimatingLibraryService.updateAssemblyComponent(versionId, modal.record.id, v);
      }
      return epcEstimatingLibraryService.createAssemblyComponent(versionId, v);
    },
    onSuccess: () => {
      componentForm.resetFields();
      onMutOk(modal?.record ? 'Component updated' : 'Component added', [
        ['epc-est-lib-assembly-components', versionId!],
      ]);
    },
    onError: onMutErr,
  });
  const componentDelM = useMutation({
    mutationFn: (id: string) =>
      epcEstimatingLibraryService.deleteAssemblyComponent(versionId!, id),
    onSuccess: () =>
      onMutOk('Component deleted', [['epc-est-lib-assembly-components', versionId!]]),
    onError: onMutErr,
  });

  return (
    <div style={{ padding: 16 }}>
      <Title level={3} style={{ marginBottom: 4 }}>
        Estimating libraries
      </Title>
      <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
        Company EST3 rate books, crews, norms, and assemblies. Use from Tender Estimation Workbench
        when costing. Rate Resolver is an orchestrator over these books — not a rate master.
      </Text>

      <Tabs
        activeKey={active}
        onChange={(k) => navigate(TAB_META[k as EstimatingLibraryTab].path)}
        items={Object.entries(TAB_META).map(([key, meta]) => ({
          key,
          label: meta.label,
        }))}
        style={{ marginBottom: 12 }}
      />

      {/* ── Labour ───────────────────────────────────────────────────────── */}
      {active === 'labour' ? (
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Card
            size="small"
            title="Labour classes"
            extra={
              <Button
                type="primary"
                size="small"
                onClick={() => {
                  labourClassForm.resetFields();
                  labourClassForm.setFieldsValue({ defaultUom: 'HR' });
                  setModal({ kind: 'labour-class' });
                }}
              >
                Add class
              </Button>
            }
          >
            <Table
              size="small"
              rowKey="id"
              loading={labourClassesQ.isLoading}
              dataSource={labourClassesQ.data || []}
              pagination={false}
              columns={[
                { title: 'Code', dataIndex: 'code', width: 120 },
                { title: 'Name', dataIndex: 'name' },
                { title: 'Trade', dataIndex: 'trade_code', width: 100, render: (v) => v || '—' },
                { title: 'UOM', dataIndex: 'default_uom', width: 80 },
                {
                  title: 'Actions',
                  key: 'actions',
                  width: 140,
                  render: (_: unknown, r: any) => (
                    <Space size={4}>
                      <Button
                        type="link"
                        size="small"
                        onClick={() =>
                          openEdit('labour-class', r, labourClassForm, {
                            code: r.code,
                            name: r.name,
                            defaultUom: r.default_uom,
                            tradeCode: r.trade_code,
                            sortOrder: r.sort_order,
                          })
                        }
                      >
                        Edit
                      </Button>
                      <Popconfirm
                        title="Delete this class?"
                        onConfirm={() => labourClassDelM.mutate(r.id)}
                      >
                        <Button type="link" size="small" danger>
                          Delete
                        </Button>
                      </Popconfirm>
                    </Space>
                  ),
                },
              ]}
            />
          </Card>

          <Card
            size="small"
            title="Labour rates"
            extra={
              <Button
                type="primary"
                size="small"
                onClick={() => {
                  labourRateForm.resetFields();
                  labourRateForm.setFieldsValue({ rateBasis: 'NORMAL', currency: 'AED' });
                  setModal({ kind: 'labour-rate' });
                }}
              >
                Add rate
              </Button>
            }
          >
            <Table
              size="small"
              rowKey="id"
              loading={labourRatesQ.isLoading}
              dataSource={labourRatesQ.data || []}
              pagination={{ pageSize: 20 }}
              columns={[
                { title: 'Class', dataIndex: 'class_code', width: 120 },
                { title: 'Name', dataIndex: 'class_name' },
                { title: 'Basis', dataIndex: 'rate_basis', width: 100 },
                {
                  title: 'Rate',
                  dataIndex: 'rate',
                  width: 140,
                  render: (v, r: any) => fmtRate(v, r.currency),
                },
                { title: 'Source', dataIndex: 'rate_source', width: 120 },
                { title: 'From', dataIndex: 'effective_from', width: 120 },
                {
                  title: 'Actions',
                  key: 'actions',
                  width: 140,
                  render: (_: unknown, r: any) => (
                    <Space size={4}>
                      <Button
                        type="link"
                        size="small"
                        onClick={() =>
                          openEdit('labour-rate', r, labourRateForm, {
                            resourceClassId: r.resource_class_id,
                            rate: Number(r.rate),
                            rateBasis: r.rate_basis,
                            currency: r.currency || 'AED',
                            rateSource: r.rate_source,
                            sourceRefLabel: r.source_ref_label,
                          })
                        }
                      >
                        Edit
                      </Button>
                      <Popconfirm
                        title="Delete this rate?"
                        onConfirm={() => labourRateDelM.mutate(r.id)}
                      >
                        <Button type="link" size="small" danger>
                          Delete
                        </Button>
                      </Popconfirm>
                    </Space>
                  ),
                },
              ]}
            />
          </Card>
        </Space>
      ) : null}

      {/* ── Equipment ────────────────────────────────────────────────────── */}
      {active === 'equipment' ? (
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Card
            size="small"
            title="Equipment classes"
            extra={
              <Button
                type="primary"
                size="small"
                onClick={() => {
                  equipClassForm.resetFields();
                  equipClassForm.setFieldsValue({ ownershipMode: 'OWNED', defaultUom: 'DAY' });
                  setModal({ kind: 'equip-class' });
                }}
              >
                Add class
              </Button>
            }
          >
            <Table
              size="small"
              rowKey="id"
              loading={equipClassesQ.isLoading}
              dataSource={equipClassesQ.data || []}
              pagination={false}
              columns={[
                { title: 'Code', dataIndex: 'code', width: 120 },
                { title: 'Name', dataIndex: 'name' },
                { title: 'Ownership', dataIndex: 'ownership_mode', width: 120 },
                { title: 'UOM', dataIndex: 'default_uom', width: 80 },
                {
                  title: 'Actions',
                  key: 'actions',
                  width: 140,
                  render: (_: unknown, r: any) => (
                    <Space size={4}>
                      <Button
                        type="link"
                        size="small"
                        onClick={() =>
                          openEdit('equip-class', r, equipClassForm, {
                            code: r.code,
                            name: r.name,
                            ownershipMode: r.ownership_mode,
                            defaultUom: r.default_uom,
                            sortOrder: r.sort_order,
                          })
                        }
                      >
                        Edit
                      </Button>
                      <Popconfirm
                        title="Delete this class?"
                        onConfirm={() => equipClassDelM.mutate(r.id)}
                      >
                        <Button type="link" size="small" danger>
                          Delete
                        </Button>
                      </Popconfirm>
                    </Space>
                  ),
                },
              ]}
            />
          </Card>

          <Card
            size="small"
            title="Equipment rates"
            extra={
              <Button
                type="primary"
                size="small"
                onClick={() => {
                  equipRateForm.resetFields();
                  equipRateForm.setFieldsValue({ rateBasis: 'DAY', currency: 'AED' });
                  setModal({ kind: 'equip-rate' });
                }}
              >
                Add rate
              </Button>
            }
          >
            <Table
              size="small"
              rowKey="id"
              loading={equipRatesQ.isLoading}
              dataSource={equipRatesQ.data || []}
              pagination={{ pageSize: 20 }}
              columns={[
                { title: 'Class', dataIndex: 'class_code', width: 120 },
                { title: 'Name', dataIndex: 'class_name' },
                { title: 'Basis', dataIndex: 'rate_basis', width: 100 },
                {
                  title: 'Rate',
                  dataIndex: 'rate',
                  width: 140,
                  render: (v, r: any) => fmtRate(v, r.currency),
                },
                { title: 'Source', dataIndex: 'rate_source', width: 120 },
                {
                  title: 'Actions',
                  key: 'actions',
                  width: 140,
                  render: (_: unknown, r: any) => (
                    <Space size={4}>
                      <Button
                        type="link"
                        size="small"
                        onClick={() =>
                          openEdit('equip-rate', r, equipRateForm, {
                            equipmentClassId: r.equipment_class_id,
                            rate: Number(r.rate),
                            rateBasis: r.rate_basis,
                            currency: r.currency || 'AED',
                            rateSource: r.rate_source,
                            sourceRefLabel: r.source_ref_label,
                          })
                        }
                      >
                        Edit
                      </Button>
                      <Popconfirm
                        title="Delete this rate?"
                        onConfirm={() => equipRateDelM.mutate(r.id)}
                      >
                        <Button type="link" size="small" danger>
                          Delete
                        </Button>
                      </Popconfirm>
                    </Space>
                  ),
                },
              ]}
            />
          </Card>
        </Space>
      ) : null}

      {/* ── Crews ────────────────────────────────────────────────────────── */}
      {active === 'crews' ? (
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Card
            size="small"
            title="Crew templates"
            extra={
              <Button
                type="primary"
                size="small"
                onClick={() => {
                  crewForm.resetFields();
                  setModal({ kind: 'crew' });
                }}
              >
                Create crew
              </Button>
            }
          >
            <Table
              size="small"
              rowKey="id"
              loading={crewsQ.isLoading}
              dataSource={crewsQ.data || []}
              pagination={false}
              onRow={(r) => ({
                onClick: () => setCrewId(String(r.id)),
                style: {
                  cursor: 'pointer',
                  background: crewId === r.id ? '#e6f4ff' : undefined,
                },
              })}
              columns={[
                { title: 'Code', dataIndex: 'code', width: 120 },
                { title: 'Name', dataIndex: 'name' },
                {
                  title: 'Description',
                  dataIndex: 'description',
                  render: (v) => v || '—',
                },
                {
                  title: 'Actions',
                  key: 'actions',
                  width: 140,
                  render: (_: unknown, r: any) => (
                    <Space size={4}>
                      <Button
                        type="link"
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          openEdit('crew', r, crewForm, {
                            code: r.code,
                            name: r.name,
                            description: r.description,
                          });
                        }}
                      >
                        Edit
                      </Button>
                      <Popconfirm
                        title="Delete this crew?"
                        onConfirm={(e) => {
                          e?.stopPropagation();
                          crewDelM.mutate(r.id);
                        }}
                      >
                        <Button
                          type="link"
                          size="small"
                          danger
                          onClick={(e) => e.stopPropagation()}
                        >
                          Delete
                        </Button>
                      </Popconfirm>
                    </Space>
                  ),
                },
              ]}
            />
          </Card>

          {crewId ? (
            <Card
              size="small"
              title="Crew members"
              extra={
                <Button
                  type="primary"
                  size="small"
                  onClick={() => {
                    memberForm.resetFields();
                    memberForm.setFieldsValue({ headcount: 1 });
                    setModal({ kind: 'crew-member' });
                  }}
                >
                  Add member
                </Button>
              }
            >
              <Table
                size="small"
                rowKey="id"
                loading={crewMembersQ.isLoading}
                dataSource={crewMembersQ.data || []}
                pagination={false}
                columns={[
                  { title: 'Class', dataIndex: 'resource_class_code', width: 120 },
                  { title: 'Name', dataIndex: 'resource_class_name' },
                  {
                    title: 'Role',
                    dataIndex: 'role_label',
                    render: (v) => v || '—',
                  },
                  { title: 'Headcount', dataIndex: 'headcount', width: 100 },
                  {
                    title: 'Actions',
                    key: 'actions',
                    width: 140,
                    render: (_: unknown, r: any) => (
                      <Space size={4}>
                        <Button
                          type="link"
                          size="small"
                          onClick={() =>
                            openEdit('crew-member', r, memberForm, {
                              resourceClassId: r.resource_class_id,
                              headcount: Number(r.headcount) || 1,
                              roleLabel: r.role_label,
                              sortOrder: r.sort_order,
                            })
                          }
                        >
                          Edit
                        </Button>
                        <Popconfirm
                          title="Remove this member?"
                          onConfirm={() => memberDelM.mutate(r.id)}
                        >
                          <Button type="link" size="small" danger>
                            Delete
                          </Button>
                        </Popconfirm>
                      </Space>
                    ),
                  },
                ]}
              />
            </Card>
          ) : (
            <Text type="secondary">Select a crew to manage members.</Text>
          )}
        </Space>
      ) : null}

      {/* ── Norms ────────────────────────────────────────────────────────── */}
      {active === 'norms' ? (
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Card
            size="small"
            title="Productivity norms"
            extra={
              <Button
                type="primary"
                size="small"
                onClick={() => {
                  normForm.resetFields();
                  normForm.setFieldsValue({ outputQty: 1, periodBasis: 'DAY' });
                  setModal({ kind: 'norm' });
                }}
              >
                Create norm
              </Button>
            }
          >
            <Table
              size="small"
              rowKey="id"
              loading={normsQ.isLoading}
              dataSource={normsQ.data || []}
              pagination={{ pageSize: 20 }}
              columns={[
                { title: 'Code', dataIndex: 'code', width: 120 },
                { title: 'Name', dataIndex: 'name' },
                { title: 'Output', dataIndex: 'output_qty', width: 90 },
                { title: 'UOM', dataIndex: 'output_uom', width: 80 },
                { title: 'Period', dataIndex: 'period_basis', width: 90 },
                {
                  title: 'Actions',
                  key: 'actions',
                  width: 140,
                  render: (_: unknown, r: any) => (
                    <Space size={4}>
                      <Button
                        type="link"
                        size="small"
                        onClick={() =>
                          openEdit('norm', r, normForm, {
                            code: r.code,
                            name: r.name,
                            activityDesc: r.activity_desc,
                            outputQty: Number(r.output_qty) || 1,
                            outputUom: r.output_uom,
                            periodBasis: r.period_basis || 'DAY',
                            defaultCrewTemplateId: r.default_crew_template_id,
                          })
                        }
                      >
                        Edit
                      </Button>
                      <Popconfirm
                        title="Delete this norm?"
                        onConfirm={() => normDelM.mutate(r.id)}
                      >
                        <Button type="link" size="small" danger>
                          Delete
                        </Button>
                      </Popconfirm>
                    </Space>
                  ),
                },
              ]}
            />
          </Card>
        </Space>
      ) : null}

      {/* ── Assemblies ───────────────────────────────────────────────────── */}
      {active === 'assemblies' ? (
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Card
            size="small"
            title="Assemblies"
            extra={
              <Button
                type="primary"
                size="small"
                onClick={() => {
                  assemblyForm.resetFields();
                  assemblyForm.setFieldsValue({ outputQty: 1, outputUom: 'EA' });
                  setModal({ kind: 'assembly' });
                }}
              >
                Create assembly
              </Button>
            }
          >
            <Table
              size="small"
              rowKey="id"
              loading={assembliesQ.isLoading}
              dataSource={assembliesQ.data || []}
              pagination={false}
              onRow={(r) => ({
                onClick: () => {
                  setAssemblyId(String(r.id));
                  setVersionId(null);
                },
                style: {
                  cursor: 'pointer',
                  background: assemblyId === r.id ? '#e6f4ff' : undefined,
                },
              })}
              columns={[
                { title: 'Code', dataIndex: 'code', width: 120 },
                { title: 'Name', dataIndex: 'name' },
                {
                  title: 'Output',
                  width: 100,
                  render: (_: unknown, r: any) =>
                    `${r.output_qty ?? 1} ${r.output_uom || 'EA'}`,
                },
                {
                  title: 'Versions',
                  dataIndex: 'version_count',
                  width: 90,
                },
                {
                  title: 'Published',
                  dataIndex: 'published_version_no',
                  width: 90,
                  render: (v) => (v != null ? `v${v}` : '—'),
                },
                {
                  title: 'Status',
                  dataIndex: 'status',
                  width: 100,
                  render: (v) => <Tag>{v || 'ACTIVE'}</Tag>,
                },
                {
                  title: 'Actions',
                  key: 'actions',
                  width: 140,
                  render: (_: unknown, r: any) => (
                    <Space size={4}>
                      <Button
                        type="link"
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          openEdit('assembly', r, assemblyForm, {
                            code: r.code,
                            name: r.name,
                            description: r.description,
                            outputQty: Number(r.output_qty) || 1,
                            outputUom: r.output_uom || 'EA',
                            disciplineCode: r.discipline_code,
                            tradeCode: r.trade_code,
                            status: r.status,
                          });
                        }}
                      >
                        Edit
                      </Button>
                      <Popconfirm
                        title="Delete this assembly?"
                        onConfirm={(e) => {
                          e?.stopPropagation();
                          assemblyDelM.mutate(r.id);
                        }}
                      >
                        <Button
                          type="link"
                          size="small"
                          danger
                          onClick={(e) => e.stopPropagation()}
                        >
                          Delete
                        </Button>
                      </Popconfirm>
                    </Space>
                  ),
                },
              ]}
            />
          </Card>

          {assemblyId ? (
            <Card
              size="small"
              title="Versions"
              extra={
                <Button
                  type="primary"
                  size="small"
                  loading={versionCreateM.isPending}
                  onClick={() => versionCreateM.mutate()}
                >
                  New version
                </Button>
              }
            >
              <Table
                size="small"
                rowKey="id"
                loading={assemblyDetailQ.isLoading}
                dataSource={versions}
                pagination={false}
                onRow={(r) => ({
                  onClick: () => setVersionId(String(r.id)),
                  style: {
                    cursor: 'pointer',
                    background: versionId === r.id ? '#e6f4ff' : undefined,
                  },
                })}
                columns={[
                  {
                    title: 'Version',
                    dataIndex: 'version_no',
                    width: 90,
                    render: (v) => `v${v}`,
                  },
                  {
                    title: 'Status',
                    dataIndex: 'status',
                    width: 120,
                    render: (v) => {
                      const color =
                        v === 'PUBLISHED' ? 'green' : v === 'DRAFT' ? 'blue' : 'default';
                      return <Tag color={color}>{v}</Tag>;
                    },
                  },
                  {
                    title: 'Published at',
                    dataIndex: 'published_at',
                    render: (v) => v || '—',
                  },
                  {
                    title: 'Actions',
                    key: 'actions',
                    width: 120,
                    render: (_: unknown, r: any) =>
                      String(r.status) === 'DRAFT' ? (
                        <Popconfirm
                          title="Publish this draft version?"
                          onConfirm={(e) => {
                            e?.stopPropagation();
                            versionPublishM.mutate(r.id);
                          }}
                        >
                          <Button
                            type="link"
                            size="small"
                            loading={versionPublishM.isPending}
                            onClick={(e) => e.stopPropagation()}
                          >
                            Publish
                          </Button>
                        </Popconfirm>
                      ) : (
                        <Text type="secondary">—</Text>
                      ),
                  },
                ]}
              />
            </Card>
          ) : (
            <Text type="secondary">Select an assembly to manage versions.</Text>
          )}

          {versionId ? (
            <Card
              size="small"
              title={
                <Space>
                  <span>Components</span>
                  <Tag color={versionIsDraft ? 'blue' : 'default'}>
                    {selectedVersion?.status || '—'}
                  </Tag>
                  {!versionIsDraft ? (
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Read-only (publish creates a new draft to edit)
                    </Text>
                  ) : null}
                </Space>
              }
              extra={
                versionIsDraft ? (
                  <Button
                    type="primary"
                    size="small"
                    onClick={() => {
                      componentForm.resetFields();
                      componentForm.setFieldsValue({
                        resourceKind: 'MATERIAL',
                        qtyPerOutput: 1,
                        currency: 'AED',
                        wasteFactorDefault: 0,
                      });
                      setModal({ kind: 'component' });
                    }}
                  >
                    Add component
                  </Button>
                ) : null
              }
            >
              <Table
                size="small"
                rowKey="id"
                loading={componentsQ.isLoading}
                dataSource={componentsQ.data || []}
                pagination={false}
                columns={[
                  { title: 'Kind', dataIndex: 'resource_kind', width: 110 },
                  {
                    title: 'Description',
                    dataIndex: 'description',
                    render: (v) => v || '—',
                  },
                  { title: 'Qty / out', dataIndex: 'qty_per_output', width: 90 },
                  { title: 'UOM', dataIndex: 'uom', width: 70 },
                  {
                    title: 'Rate',
                    dataIndex: 'default_rate',
                    width: 120,
                    render: (v, r: any) =>
                      v != null ? fmtRate(v, r.currency) : '—',
                  },
                  {
                    title: 'Ref',
                    dataIndex: 'resource_ref_label',
                    render: (v) => v || '—',
                  },
                  ...(versionIsDraft
                    ? [
                        {
                          title: 'Actions',
                          key: 'actions',
                          width: 140,
                          render: (_: unknown, r: any) => (
                            <Space size={4}>
                              <Button
                                type="link"
                                size="small"
                                onClick={() =>
                                  openEdit('component', r, componentForm, {
                                    resourceKind: r.resource_kind,
                                    description: r.description,
                                    qtyPerOutput: Number(r.qty_per_output) || 1,
                                    uom: r.uom,
                                    wasteFactorDefault: Number(r.waste_factor_default) || 0,
                                    resourceRefType: r.resource_ref_type,
                                    resourceRefId: r.resource_ref_id,
                                    resourceRefLabel: r.resource_ref_label,
                                    defaultRate:
                                      r.default_rate != null ? Number(r.default_rate) : undefined,
                                    currency: r.currency || 'AED',
                                    rateSource: r.rate_source,
                                    sortOrder: r.sort_order,
                                  })
                                }
                              >
                                Edit
                              </Button>
                              <Popconfirm
                                title="Delete this component?"
                                onConfirm={() => componentDelM.mutate(r.id)}
                              >
                                <Button type="link" size="small" danger>
                                  Delete
                                </Button>
                              </Popconfirm>
                            </Space>
                          ),
                        },
                      ]
                    : []),
                ]}
              />
            </Card>
          ) : assemblyId ? (
            <Text type="secondary">Select a version to manage components.</Text>
          ) : null}
        </Space>
      ) : null}

      {/* ── Rate Resolver (informational) ────────────────────────────────── */}
      {active === 'rate-resolver' ? (
        <Card>
          <Tag color="blue">Orchestrator</Tag>
          <Title level={4} style={{ marginTop: 12 }}>
            Rate Source Resolver
          </Title>
          <Paragraph>
            The Rate Resolver is an <Text strong>orchestrator</Text> over Inventory, EST3 rate
            books, and Procurement quotes — not a rate master. Maintain rates in the labour and
            equipment books; the workbench resolves candidates at costing time.
          </Paragraph>
          <Paragraph type="secondary">
            Spec: <Text code>docs/estimation/nextgen/phase2/08-RATE-SOURCE-RESOLVER-V1.md</Text>
          </Paragraph>
          <Space>
            <Button type="link" style={{ padding: 0 }}>
              <Link to="/epc/libraries/labour">Labour rate book</Link>
            </Button>
            <Button type="link" style={{ padding: 0 }}>
              <Link to="/epc/libraries/equipment">Equipment rate book</Link>
            </Button>
          </Space>
        </Card>
      ) : null}

      {/* ── Modals ───────────────────────────────────────────────────────── */}
      <Modal
        title={modal?.record ? 'Edit labour class' : 'Add labour class'}
        open={modal?.kind === 'labour-class'}
        onCancel={closeModal}
        onOk={() => labourClassForm.submit()}
        confirmLoading={labourClassSaveM.isPending}
        destroyOnClose
      >
        <Form form={labourClassForm} layout="vertical" onFinish={(v) => labourClassSaveM.mutate(v)}>
          <Form.Item name="code" label="Code" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="name" label="Name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="defaultUom" label="Default UOM">
            <Input placeholder="HR" />
          </Form.Item>
          <Form.Item name="tradeCode" label="Trade code">
            <Input />
          </Form.Item>
          <Form.Item name="sortOrder" label="Sort order">
            <InputNumber style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={modal?.record ? 'Edit labour rate' : 'Add labour rate'}
        open={modal?.kind === 'labour-rate'}
        onCancel={closeModal}
        onOk={() => labourRateForm.submit()}
        confirmLoading={labourRateSaveM.isPending}
        destroyOnClose
      >
        <Form form={labourRateForm} layout="vertical" onFinish={(v) => labourRateSaveM.mutate(v)}>
          {!modal?.record ? (
            <Form.Item name="resourceClassId" label="Labour class" rules={[{ required: true }]}>
              <Select options={labourClassOptions} loading={labourClassesQ.isLoading} />
            </Form.Item>
          ) : null}
          <Form.Item name="rate" label="Rate" rules={[{ required: true }]}>
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="rateBasis" label="Basis">
            <Select
              options={[
                { value: 'NORMAL', label: 'NORMAL' },
                { value: 'OT', label: 'OT' },
                { value: 'SHIFT', label: 'SHIFT' },
                { value: 'ALLOWANCE', label: 'ALLOWANCE' },
              ]}
            />
          </Form.Item>
          <Form.Item name="currency" label="Currency">
            <Input />
          </Form.Item>
          <Form.Item name="rateSource" label="Rate source">
            <Input />
          </Form.Item>
          <Form.Item name="sourceRefLabel" label="Source ref">
            <Input />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={modal?.record ? 'Edit equipment class' : 'Add equipment class'}
        open={modal?.kind === 'equip-class'}
        onCancel={closeModal}
        onOk={() => equipClassForm.submit()}
        confirmLoading={equipClassSaveM.isPending}
        destroyOnClose
      >
        <Form form={equipClassForm} layout="vertical" onFinish={(v) => equipClassSaveM.mutate(v)}>
          <Form.Item name="code" label="Code" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="name" label="Name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="ownershipMode" label="Ownership">
            <Select
              options={[
                { value: 'OWNED', label: 'OWNED' },
                { value: 'HIRED', label: 'HIRED' },
                { value: 'MIXED', label: 'MIXED' },
              ]}
            />
          </Form.Item>
          <Form.Item name="defaultUom" label="Default UOM">
            <Input placeholder="DAY" />
          </Form.Item>
          <Form.Item name="sortOrder" label="Sort order">
            <InputNumber style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={modal?.record ? 'Edit equipment rate' : 'Add equipment rate'}
        open={modal?.kind === 'equip-rate'}
        onCancel={closeModal}
        onOk={() => equipRateForm.submit()}
        confirmLoading={equipRateSaveM.isPending}
        destroyOnClose
      >
        <Form form={equipRateForm} layout="vertical" onFinish={(v) => equipRateSaveM.mutate(v)}>
          {!modal?.record ? (
            <Form.Item
              name="equipmentClassId"
              label="Equipment class"
              rules={[{ required: true }]}
            >
              <Select options={equipClassOptions} loading={equipClassesQ.isLoading} />
            </Form.Item>
          ) : null}
          <Form.Item name="rate" label="Rate" rules={[{ required: true }]}>
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="rateBasis" label="Basis">
            <Input placeholder="DAY" />
          </Form.Item>
          <Form.Item name="currency" label="Currency">
            <Input />
          </Form.Item>
          <Form.Item name="rateSource" label="Rate source">
            <Input />
          </Form.Item>
          <Form.Item name="sourceRefLabel" label="Source ref">
            <Input />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={modal?.record ? 'Edit crew' : 'Create crew'}
        open={modal?.kind === 'crew'}
        onCancel={closeModal}
        onOk={() => crewForm.submit()}
        confirmLoading={crewSaveM.isPending}
        destroyOnClose
      >
        <Form form={crewForm} layout="vertical" onFinish={(v) => crewSaveM.mutate(v)}>
          <Form.Item name="code" label="Code" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="name" label="Name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={modal?.record ? 'Edit member' : 'Add member'}
        open={modal?.kind === 'crew-member'}
        onCancel={closeModal}
        onOk={() => memberForm.submit()}
        confirmLoading={memberSaveM.isPending}
        destroyOnClose
      >
        <Form form={memberForm} layout="vertical" onFinish={(v) => memberSaveM.mutate(v)}>
          <Form.Item name="resourceClassId" label="Labour class" rules={[{ required: true }]}>
            <Select options={labourClassOptions} loading={labourClassesQ.isLoading} />
          </Form.Item>
          <Form.Item name="headcount" label="Headcount" rules={[{ required: true }]}>
            <InputNumber min={1} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="roleLabel" label="Role">
            <Input />
          </Form.Item>
          <Form.Item name="sortOrder" label="Sort order">
            <InputNumber style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={modal?.record ? 'Edit productivity norm' : 'Create productivity norm'}
        open={modal?.kind === 'norm'}
        onCancel={closeModal}
        onOk={() => normForm.submit()}
        confirmLoading={normSaveM.isPending}
        destroyOnClose
      >
        <Form form={normForm} layout="vertical" onFinish={(v) => normSaveM.mutate(v)}>
          <Form.Item name="code" label="Code" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="name" label="Name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="activityDesc" label="Activity description">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item name="outputQty" label="Output qty">
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="outputUom" label="Output UOM">
            <Input />
          </Form.Item>
          <Form.Item name="periodBasis" label="Period basis">
            <Select
              options={[
                { value: 'DAY', label: 'DAY' },
                { value: 'SHIFT', label: 'SHIFT' },
                { value: 'HOUR', label: 'HOUR' },
              ]}
            />
          </Form.Item>
          <Form.Item name="defaultCrewTemplateId" label="Default crew">
            <Select allowClear options={crewOptions} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={modal?.record ? 'Edit assembly' : 'Create assembly'}
        open={modal?.kind === 'assembly'}
        onCancel={closeModal}
        onOk={() => assemblyForm.submit()}
        confirmLoading={assemblySaveM.isPending}
        destroyOnClose
      >
        <Form form={assemblyForm} layout="vertical" onFinish={(v) => assemblySaveM.mutate(v)}>
          <Form.Item name="code" label="Code" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="name" label="Name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item name="outputQty" label="Output qty">
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="outputUom" label="Output UOM">
            <Input />
          </Form.Item>
          <Form.Item name="disciplineCode" label="Discipline">
            <Input />
          </Form.Item>
          <Form.Item name="tradeCode" label="Trade">
            <Input />
          </Form.Item>
          {modal?.record ? (
            <Form.Item name="status" label="Status">
              <Select
                options={[
                  { value: 'ACTIVE', label: 'ACTIVE' },
                  { value: 'RETIRED', label: 'RETIRED' },
                ]}
              />
            </Form.Item>
          ) : null}
        </Form>
      </Modal>

      <Modal
        title={modal?.record ? 'Edit component' : 'Add component'}
        open={modal?.kind === 'component'}
        onCancel={closeModal}
        onOk={() => componentForm.submit()}
        confirmLoading={componentSaveM.isPending}
        destroyOnClose
      >
        <Form form={componentForm} layout="vertical" onFinish={(v) => componentSaveM.mutate(v)}>
          <Form.Item name="resourceKind" label="Resource kind" rules={[{ required: true }]}>
            <Select options={RESOURCE_KIND_OPTS} />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input />
          </Form.Item>
          <Form.Item name="qtyPerOutput" label="Qty per output">
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="uom" label="UOM">
            <Input />
          </Form.Item>
          <Form.Item name="wasteFactorDefault" label="Waste factor">
            <InputNumber min={0} step={0.01} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="resourceRefLabel" label="Resource label">
            <Input />
          </Form.Item>
          <Form.Item name="resourceRefType" label="Resource ref type">
            <Input />
          </Form.Item>
          <Form.Item name="resourceRefId" label="Resource ref id">
            <Input />
          </Form.Item>
          <Form.Item name="defaultRate" label="Default rate">
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="currency" label="Currency">
            <Input />
          </Form.Item>
          <Form.Item name="rateSource" label="Rate source">
            <Input />
          </Form.Item>
          <Form.Item name="sortOrder" label="Sort order">
            <InputNumber style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default EpcEstimatingLibrariesPage;
